import React, { useEffect, useRef, useState } from 'react';
import { DashboardTabs, useDashboardStore } from '@features/dashboard';
import FilterBar from '../components/FilterBar.tsx';
import {
  IntegrationsStatsCards
} from '@features/dashboard/components/dashboardIntegrations/IntegrationsStatsCards.tsx';
import SalesforceJobsCard from '@features/dashboard/components/dashboardIntegrations/SalesforceJobsCard.tsx';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { IntegrationCodes, Integrations } from '@features/Settings/enums/IntegrationsEnums.ts';
import { HubspotLogoTyped, OutreachLogoTyped, SalesforceLogoTyped } from '@assets/index.ts';
import type { CurrentUser, HubspotAccount, OutreachAccount, SalesforceAccount } from '@types';
import useSalesforceApi from '@features/Settings/hooks/useSalesforce.Api.ts';
import { useSessionStore } from '@features/auth';
import HubspotJobsCard from '@features/dashboard/components/dashboardIntegrations/HubspotJobsCard.tsx';
import UseOutreachApi from '@features/Settings/hooks/useOutreachApi.ts';
import { useHubspotApi } from '@features/Settings/hooks/useHubspotApi.ts';
import OutreachJobsCard from '@features/dashboard/components/dashboardIntegrations/OutreachJobsCard.tsx';
import { CardDescription, CardHeader, CardTitle } from '@core-ui/Components/ui/card.tsx';
import { useTranslation } from 'react-i18next';
import useDashboardIntegrationsViewModel from '@features/dashboard/hooks/useDashboardIntegrationsViewModel.ts';
import { logError } from '@lib/logger/logError.ts';
import { logSuccess } from '@lib/logger/logSuccess.tsx';

export default function DashboardIntegrationsPage (): React.JSX.Element {
  const { t } = useTranslation();
  const featureFlags = useFeatureFlagsStore((s) => s.flags);
  const currentUser = useSessionStore((sessionState) => sessionState.currentUser);
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;
  const organizationId = currentUser?.organizationId ?? '';
  const { getAllHubspotAccounts } = useHubspotApi();
  const { getAllOutreachAccounts } = UseOutreachApi();
  const { getConnectedAccounts } = useSalesforceApi();
  const [hubspotAccounts, setHubspotAccounts] = useState<HubspotAccount[]>([]);
  const [outreachAccounts, setOutreachAccounts] = useState<OutreachAccount[]>([]);
  const [salesforceAccounts, setSalesforceAccounts] = useState<SalesforceAccount[]>([]);
  
  const [connectionHubspotTried, setConnectionHubspotTried] = useState(true);
  const [connectionOutreachTried, setConnectionOutreachTried] = useState(true);
  const [connectionSalesforceTried, setConnectionSalesforceTried] = useState(true);

  const [isRetryingHubspotJob, setIsRetryingHubspotJob] = useState(false);
  const [isRetryingOutreachJob, setIsRetryingOutreachJob] = useState(false);
  const [isRetryingSalesforceJob, setIsRetryingSalesforceJob] = useState(false);

  const [openCard, setOpenCard] = useState('hubspot');
  const isFirstRender = useRef(true);
  
  const {
    selectedHubspotJobs,
    selectedOutreachJobs,
    selectedSalesforceJobs,
    currentPage,
    totalStatusSuccess,
    totalStatusCreated,
    totalStatusFailed,
    totalCountJobs,
    totalCountHs,
    totalCountOr,
    totalCountSf,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
    isLoadingHubspotJobs,
    isLoadingOutreachJobs,
    isLoadingSalesforceJobs,
    currentUserId,
    organizations,
    isTendrilSelected,
    openHubspotJobs,
    openOutreachJobs,
    openSalesforceJobs,
    retryHubspotJob,
    retryOutreachJob,
    retrySalesforceJob
  } = useDashboardIntegrationsViewModel();

  const {
    filters
  } = useDashboardStore();

  const threeSeconds = 1000 * 3;
  
  const integrations = {
    outreach: {
      code: IntegrationCodes.Outreach,
      name: Integrations.Outreach,
      icon: OutreachLogoTyped,
    },
    salesforce: {
      code: IntegrationCodes.Salesforce,
      name: Integrations.Salesforce,
      icon: SalesforceLogoTyped,
    },
    hubspot: {
      code:  IntegrationCodes.Hubspot,
      name: Integrations.Hubspot,
      icon: HubspotLogoTyped,
    },
  };

  async function fetchHubspotAccounts (user: CurrentUser): Promise<void> {
    const { userId, organizationId } = user;

    const { data: hubspotAccounts } = await getAllHubspotAccounts(userId, organizationId);

    if (hubspotAccounts !== undefined) {
      setConnectionHubspotTried(false);
      setHubspotAccounts(hubspotAccounts);
    }
  }

  async function fetchOutreachAccounts (user: CurrentUser): Promise<void> {
    const { userId, organizationId } = user;

    const { data: outreachAccounts } = await getAllOutreachAccounts(userId, organizationId);

    if (outreachAccounts !== undefined) {
      setConnectionOutreachTried(false);
      setOutreachAccounts(outreachAccounts);
    }
  }

  async function fetchSalesforceAccounts (): Promise<void> {
    const { data: salesforceAccount } = await getConnectedAccounts(currentUserId, organizationId);

    if (salesforceAccount !== undefined) {
      setConnectionSalesforceTried(false);
      setSalesforceAccounts(salesforceAccount);
    }
  }

  useEffect(() => {
    if (!featureFlags.hubspot) {
      return;
    }
    if (currentUser === null) {
      throw new Error('No current user found in session store.');
    }
    if (!connectionHubspotTried) {
      return ()=> {};
    } else {
      
      let tries = 0;
      const fetchInterval = setInterval(() => {
        if ((featureFlags.outreach || featureFlags.hubspot) && tries < 3) {
          void fetchHubspotAccounts(currentUser);
          tries++;
        }
      }, threeSeconds);

      return () => {
        clearInterval(fetchInterval);
      };
    }
  }, [connectionHubspotTried, featureFlags.hubspot]);

  useEffect(() => {
    if (!featureFlags.outreach) {
      return;
    }
    if (currentUser === null) {
      throw new Error('No current user found in session store.');
    }
    if (!connectionOutreachTried) {
      return ()=> {};
    } else {
      
      let tries = 0;
      const fetchInterval = setInterval(() => {
        if ((featureFlags.outreach || featureFlags.hubspot) && tries < 3) {
          void fetchOutreachAccounts(currentUser);
          tries++;
        }
      }, threeSeconds);

      return () => {
        clearInterval(fetchInterval);
      };
    }
  }, [connectionOutreachTried, featureFlags.outreach]);

  useEffect(() => {
    if (!featureFlags.salesforce) {
      return;
    }
    if (currentUser === null) {
      throw new Error('No current user found in session store.');
    }
    if (!connectionSalesforceTried) {
      return () => {
      };
    } else {

      let tries = 0;
      const fetchInterval = setInterval(() => {
        if (featureFlags.salesforce && tries < 3) {
          void fetchSalesforceAccounts();
          tries++;
        }
      }, threeSeconds);

      return () => {
        clearInterval(fetchInterval);
      };
    }
  }, [connectionSalesforceTried, featureFlags.salesforce]);

  useEffect(() => {
    if (!featureFlags.hubspot) {
      return;
    }
    if (filters.listTypes.length === 0 || filters.listTypes.includes('HUBSPOT')) {
      void openHubspotJobs().catch((error) => {
        void logError(
          t('dashboard.integrations.toasts.error.loadHubspotJobs'),
          'DashboardIntegrationsPage',
          { error: error as Error },
          true
        );
      });
    }
  }, [connectionHubspotTried, featureFlags.hubspot, filters]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!featureFlags.hubspot || openCard !== 'hubspot' ) {
      return;
    }
    if (filters.listTypes.length === 0 || filters.listTypes.includes('HUBSPOT')) {
      void openHubspotJobs().catch((error) => {
        void logError(
          t('dashboard.integrations.toasts.error.loadHubspotJobs'),
          'DashboardIntegrationsPage',
          { error: error as Error },
          true
        );
      });
    }
  }, [currentPage, rowsPerPage, openCard]);

  useEffect(() => {
    if (!featureFlags.outreach) {
      return;
    }
    if (filters.listTypes.length === 0 || filters.listTypes.includes('OUTREACH')) {
      void openOutreachJobs().catch((error) => {
        void logError(
          t('dashboard.integrations.toasts.error.loadOutreachJobs'),
          'DashboardIntegrationsPage',
          { error: error as Error },
          true
        );
      });
    }
  }, [connectionOutreachTried, featureFlags.outreach, filters]);

  useEffect(() => {
    if (!featureFlags.outreach || openCard !== 'outreach') {
      return;
    }
    if (filters.listTypes.length === 0 || filters.listTypes.includes('OUTREACH')) {
      void openOutreachJobs().catch((error) => {
        void logError(
          t('dashboard.integrations.toasts.error.loadOutreachJobs'),
          'DashboardIntegrationsPage',
          { error: error as Error },
          true
        );
      });
    }
  }, [currentPage, rowsPerPage, openCard]);

  useEffect(() => {
    if (!featureFlags.salesforce) {
      return;
    }
    if (filters.listTypes.length === 0 || filters.listTypes.includes('SALESFORCE')) {
      void openSalesforceJobs().catch((error) => {
        void logError(
          t('dashboard.integrations.toasts.error.loadSalesforceJobs'),
          'DashboardIntegrationsPage',
          { error: error as Error },
          true
        );
      });
    }
  }, [connectionSalesforceTried, featureFlags.salesforce, filters]);

  useEffect(() => {
    if (!featureFlags.salesforce || openCard !== 'salesforce') {
      return;
    }
    if (filters.listTypes.length === 0 || filters.listTypes.includes('SALESFORCE')) {
      void openSalesforceJobs().catch((error) => {
        void logError(
          t('dashboard.integrations.toasts.error.loadSalesforceJobs'),
          'DashboardIntegrationsPage',
          { error: error as Error },
          true
        );
      });
    }
  }, [currentPage, rowsPerPage, openCard]);

  useEffect(() => {
    if (featureFlags.hubspot && (filters.listTypes.length === 0 || filters.listTypes.includes('HUBSPOT'))) {
      setOpenCard('hubspot');
    } else if (featureFlags.outreach && filters.listTypes.includes('OUTREACH')) {
      setOpenCard('outreach');
    } else if (featureFlags.salesforce && filters.listTypes.includes('SALESFORCE')) {
      setOpenCard('salesforce');
    }
  }, [featureFlags, filters.listTypes]);

  const handleRetryHubspotJob = async (jobId: string, includeSkipped = false): Promise<void> => {
    setIsRetryingHubspotJob(true);
    try {
      await retryHubspotJob(jobId, includeSkipped);
      void logSuccess( t('dashboard.integrations.toasts.success.retryHubspotJob'), 'DashboardIntegrationsPage.handleRetryHubspotJob', { jobId, includeSkipped }, true );
    } catch (error) {
      void logError(t('dashboard.integrations.toasts.error.retryHubspotJob'), 'DashboardIntegrationsViewModel.retryHubspotJob', { error, jobId, includeSkipped }, true);
    } finally {
      setIsRetryingHubspotJob(false);
    }
  };

  const handleRetryOutreachJob = async (jobId: string): Promise<void> => {
    setIsRetryingOutreachJob(true);
    try {
      await retryOutreachJob(jobId);
      void logSuccess(t('dashboard.integrations.toasts.success.retryOutreachJob'), 'DashboardIntegrationsPage.handleRetryOutreachJob', { jobId }, true );
    } catch (error) {
      void logError(t('dashboard.integrations.toasts.error.retryOutreachJob'), 'DashboardIntegrationsPage.retryOutreachJob', { error, jobId }, true);
    } finally {
      setIsRetryingOutreachJob(false);
    }
  };

  const handleRetrySalesforceJob = async (jobId: string): Promise<void> => {
    setIsRetryingSalesforceJob(true);
    try {
      await retrySalesforceJob(jobId);
      void logSuccess(t('dashboard.integrations.toasts.success.retrySalesforceJob'), 'DashboardIntegrationsPage.handleRetrySalesforceJob', { jobId }, true );
    } catch (error) {
      void logError(t('dashboard.integrations.toasts.error.retrySalesforceJob'), 'DashboardIntegrationsPage.retrySalesforceJob', { error, jobId }, true);
    } finally {
      setIsRetryingSalesforceJob(false);
    }
  };

  return (
    <div className="space-y-4">
      <DashboardTabs />

      <FilterBar isSuperAdmin={isSuperAdmin} organizations={organizations} forIntegrationsPage={true}/>
      
      <CardHeader className="px-0 flex justify-between items-end">
        <div>
          <CardTitle className="text-2xl font-bold text-foreground">{t('dashboard.integrations.header.title')}</CardTitle>
          <CardDescription className="mt-1">
            {t('dashboard.integrations.header.description')}
          </CardDescription>
        </div>
      </CardHeader>

      <IntegrationsStatsCards totalJobs={totalCountJobs} totalSuccessful={totalStatusSuccess} totalFailed={totalStatusFailed} totalCreated={totalStatusCreated} />

      {featureFlags.hubspot && (filters.listTypes.includes('HUBSPOT') || filters.listTypes.length === 0) && (
        <HubspotJobsCard
          name={integrations.hubspot.name}
          code={integrations.hubspot.code}
          icon={integrations.hubspot.icon}
          connected={hubspotAccounts.length > 0}
          isSuperAdmin={isSuperAdmin}
          isTendrilSelected={isTendrilSelected}
          jobs={selectedHubspotJobs}
          isLoading={isLoadingHubspotJobs}
          isRetrying={isRetryingHubspotJob}
          currentPage={currentPage}
          totalCount={totalCountHs}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
          open={openCard === 'hubspot'}
          onOpenChange={(open) => {
            setOpenCard(open ? 'hubspot' : '');
          }}
          onRetryJob={handleRetryHubspotJob}
        />
      )}

      {featureFlags.outreach && (filters.listTypes.includes('OUTREACH') || filters.listTypes.length === 0) && (
        <OutreachJobsCard
          name={integrations.outreach.name}
          code={integrations.outreach.code}
          icon={integrations.outreach.icon}
          connected={outreachAccounts.length > 0}
          isSuperAdmin={isSuperAdmin}
          isTendrilSelected={isTendrilSelected}
          jobs={selectedOutreachJobs}
          isLoading={isLoadingOutreachJobs}
          isRetrying={isRetryingOutreachJob}
          currentPage={currentPage}
          totalCount={totalCountOr}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
          open={openCard === 'outreach'}
          onOpenChange={(open) => {
            setOpenCard(open ? 'outreach' : '');
          }}
          onRetryJob={handleRetryOutreachJob}
        />
      )}

      {featureFlags.salesforce && (filters.listTypes.includes('SALESFORCE') || filters.listTypes.length === 0) && (
        <SalesforceJobsCard
          name={integrations.salesforce.name}
          code={integrations.salesforce.code}
          icon={integrations.salesforce.icon}
          connected={salesforceAccounts.length > 0}
          isSuperAdmin={isSuperAdmin}
          isTendrilSelected={isTendrilSelected}
          jobs={selectedSalesforceJobs}
          isLoading={isLoadingSalesforceJobs}
          isRetrying={isRetryingSalesforceJob}
          currentPage={currentPage}
          totalCount={totalCountSf}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
          open={openCard === 'salesforce'}
          onOpenChange={(open) => {
            setOpenCard(open ? 'salesforce' : '');
          }}
          onRetryJob={handleRetrySalesforceJob}
        />
      )}
    </div>
  );
}